import type { LensCardView } from '~/types/leLens';

import type { LensSelection } from './cardView';

type CardWhereEntry = NonNullable<LensCardView['where']>[number];

export type CardFilterReach = {
  narrowedBy: Array<string>;
  unreachable: Array<string>;
  pinned: Array<CardWhereEntry>;
};

function pinningEntry(where: LensCardView['where'], column: string, values: Array<string>): CardWhereEntry | undefined {
  const pins = (where ?? []).filter((entry) => entry.column === column);
  const excluded = (value: string) =>
    pins.some((entry) => (entry.in?.length && !entry.in.includes(value)) || entry.notIn?.includes(value));
  if (!values.length || !values.every(excluded)) {
    return undefined;
  }
  const kept = pins.find((entry) => entry.in?.length)?.in?.filter((value) => !excluded(value));
  return kept?.length
    ? { column, in: kept }
    : { column, notIn: [...new Set(pins.flatMap((entry) => entry.notIn ?? []))] };
}

export function selectionForCard(
  selection: LensSelection | undefined,
  where: LensCardView['where'],
): LensSelection | undefined {
  const pinned = Object.entries(selection ?? {}).filter(([column, values]) => pinningEntry(where, column, values));
  if (!pinned.length) {
    return selection;
  }
  return Object.fromEntries(
    Object.entries(selection ?? {}).filter(([column]) => !pinned.some(([pinnedColumn]) => pinnedColumn === column)),
  );
}

export function cardFilterReach(
  selection: LensSelection | undefined,
  where: LensCardView['where'],
  data: { columns?: Array<string>; appliedFilterColumns?: Array<string> } | undefined,
): CardFilterReach {
  const pinned: Array<CardWhereEntry> = [];
  const selected: Array<string> = [];
  for (const [column, values] of Object.entries(selection ?? {})) {
    const entry = pinningEntry(where, column, values);
    if (entry) {
      pinned.push(entry);
    } else {
      selected.push(column);
    }
  }
  const narrowedBy = [
    ...new Set([
      ...(data?.appliedFilterColumns ?? []),
      ...selected.filter((column) => data?.columns?.includes(column)),
    ]),
  ];
  return { narrowedBy, unreachable: selected.filter((column) => !narrowedBy.includes(column)), pinned };
}
